import { DateTime } from 'luxon';
import {
  DUTY_ANALYTICS_FUTURE_END_REFUSAL,
  DUTY_ANALYTICS_MAX_WINDOW_DAYS,
  DUTY_ANALYTICS_REVERSED_REFUSAL,
  dutyAnalyticsSpanRefusal,
} from '../constants/dutyAnalytics.constant';
import { IST_ZONE } from './slot.util';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [P7C.1 §5] DUTY ANALYTICS — THE CUSTOM WINDOW'S ONE RULE, FOR BOTH SIDES.
 *
 *  The API calls it with `istDutyDate(now)` and answers a refusal as a 422 whose message is the sentence; the
 *  web calls it with `istNow().toISODate()` and prints the same sentence under the date pickers, sending
 *  nothing. One function, so a pair the page accepts is a pair the server accepts.
 *
 *  ⚠️ THE ORDER IS THE ORDER A PERSON FIXES THINGS IN: reversed, then a future end, then the span. A reversed
 *  pair has no meaningful span, and an end after today is the date to change before counting days.
 *
 *  ⚠️ A FUTURE END IS REFUSED, NOT CLAMPED. "Left open" means not settled on a duty that has ENDED, and the
 *  page finds the duties that have not ended from the window's last day; a window ending after today would
 *  count shares still being worked as left open. A window ending in the PAST is safe as it is.
 *
 *  ⚠️ PRECONDITION: both arguments are CALENDAR dates (`YYYY-MM-DD` that exist). `istDayCount` throws a
 *  `RangeError` on `…-02-30`, so a caller holding raw URL text parses it first — the web's `calendarDateOf`,
 *  the API's `calendarDateSchema`.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

const istDayOf = (date: string): DateTime => {
  const day = DateTime.fromISO(date, { zone: IST_ZONE }).startOf('day');
  if (!day.isValid) throw new RangeError(`"${date}" is not a calendar date`);
  return day;
};

/**
 * Days from `from` to `to`, BOTH INCLUDED, counted on the IST calendar — `addIstDays`' rule: a day is a
 * calendar unit, never `86_400_000` milliseconds. `from === to` is one day.
 */
export const istDayCount = (from: string, to: string): number =>
  Math.round(istDayOf(to).diff(istDayOf(from), 'days').days) + 1;

/** The sentence refusing a custom window, or `null` when the pair may be read. */
export const dutyAnalyticsWindowRefusal = (from: string, to: string, today: string): string | null => {
  if (from > to) return DUTY_ANALYTICS_REVERSED_REFUSAL;
  if (to > today) return DUTY_ANALYTICS_FUTURE_END_REFUSAL;
  const days = istDayCount(from, to);
  return days > DUTY_ANALYTICS_MAX_WINDOW_DAYS ? dutyAnalyticsSpanRefusal(days) : null;
};
